import type { Scores } from './bareme.js';
import { declarationsCharte, type LangueCharte } from './chartes.js';
import type { GroupeBareme } from './familles.js';

/** Réponses cochées sur une charte signée : clé de déclaration → clé du choix. */
export type ReponsesDeclarations = Record<string, string>;

export interface LectureDeclarations {
  /** Notes tabac et alcool déduites de la grille ; absentes si la grille ne tranche pas. */
  scores: Pick<Scores, 'tabac' | 'alcool'>;
  /** Critère éliminatoire déclaré sur la charte (docs/BAREME.md § 3). */
  eliminatoire: { cle: 'hygiene'; raison: string } | null;
  /** Ce que la grille ne permet pas de noter : à trancher à l'entretien. */
  aTrancher: string[];
}

/** Charte éditée pour une candidature, signée ou non (GET /candidatures/:id/chartes). */
export interface CharteSigneeResume {
  id: string;
  langue: LangueCharte;
  version: number;
  editeeLe: string;
  documentViergeId: string | null;
  documentScanId: string | null;
  signeeLe: string | null;
  declarations: ReponsesDeclarations;
  lecture: LectureDeclarations | null;
}

/**
 * Postes en zone sensible pour le tabac (docs/BAREME.md § Grille : « 2 fumeur
 * régulier acceptant des plages fixes, poste en zone sensible ») : production
 * et vente manipulent des aliments, les commerciaux sont au contact des
 * clients. Les autres relèvent de « poste sans contact client » (3).
 */
const ZONE_SENSIBLE: readonly GroupeBareme[] = ['production', 'vente', 'commercial'];

export function verifierDeclarations(
  groupe: GroupeBareme,
  valeur: unknown,
): { valide: true; reponses: ReponsesDeclarations } | { valide: false; erreur: string } {
  if (!valeur || typeof valeur !== 'object' || Array.isArray(valeur)) {
    return { valide: false, erreur: 'Déclarations illisibles' };
  }
  const declarations = declarationsCharte(groupe);
  const reponses: ReponsesDeclarations = {};
  for (const [cle, choix] of Object.entries(valeur)) {
    const declaration = declarations.find((d) => d.cle === cle);
    if (!declaration || typeof choix !== 'string' || !declaration.choix.some((c) => c.cle === choix)) {
      return { valide: false, erreur: `Déclaration inconnue ou choix invalide : ${cle}` };
    }
    reponses[cle] = choix;
  }
  return { valide: true, reponses };
}

/**
 * Grille de docs/BAREME.md appliquée aux cases cochées : la charte signée est
 * la source, l'évaluation ne fait que la reprendre.
 */
export function lireDeclarations(groupe: GroupeBareme, reponses: ReponsesDeclarations): LectureDeclarations {
  const scores: Pick<Scores, 'tabac' | 'alcool'> = {};
  const aTrancher: string[] = [];
  let eliminatoire: LectureDeclarations['eliminatoire'] = null;

  const habitude = reponses['tabac.habitude'];
  const quatreHeures = reponses['tabac.quatre_heures'];
  const regles = reponses['tabac.regles'];
  if (regles === 'tabac.regles.non') {
    eliminatoire = {
      cle: 'hygiene',
      raison: 'refus des zones sans tabac et du lavage des mains, déclaré sur la charte signée',
    };
  }
  if (habitude === 'tabac.habitude.non_fumeur' || habitude === 'tabac.habitude.ancien') {
    scores.tabac = 5;
  } else if (quatreHeures === 'tabac.quatre_heures.non') {
    scores.tabac = 1;
  } else if (habitude === 'tabac.habitude.occasionnel' && quatreHeures === 'tabac.quatre_heures.oui') {
    scores.tabac = 4;
  } else if (
    habitude === 'tabac.habitude.regulier' &&
    quatreHeures === 'tabac.quatre_heures.oui' &&
    regles === 'tabac.regles.oui'
  ) {
    scores.tabac = ZONE_SENSIBLE.includes(groupe) ? 2 : 3;
  } else if (regles !== 'tabac.regles.non') {
    aTrancher.push('tabac : déclaration incomplète, note à fixer à l’entretien');
  }

  const consommation = reponses['alcool.consommation'];
  const controle = reponses['alcool.controle'];
  if (controle === 'alcool.controle.non') {
    scores.alcool = 0;
    eliminatoire ??= {
      cle: 'hygiene',
      raison:
        groupe === 'chauffeur'
          ? 'refus du contrôle d’alcoolémie avant la prise de poste, déclaré sur la charte signée'
          : 'refus de la tolérance zéro alcool pendant le service, déclaré sur la charte signée',
    };
  } else if (controle === 'alcool.controle.oui' && consommation === 'alcool.consommation.aucune') {
    scores.alcool = 5;
  } else if (controle === 'alcool.controle.oui' && consommation === 'alcool.consommation.occasionnelle') {
    scores.alcool = 3;
  } else {
    aTrancher.push(
      consommation === 'alcool.consommation.sans_reponse'
        ? 'alcool : le candidat n’a pas souhaité répondre, note à fixer à l’entretien'
        : 'alcool : déclaration incomplète, note à fixer à l’entretien',
    );
  }

  return { scores, eliminatoire, aTrancher };
}
